import { LineStyle } from 'lightweight-charts'
import BadgeComponent from './Badge'
import PanelComponent from './Panel'
import { BandFillSeries } from './BandFillSeries'

function toSeconds(t) {
  return t > 1e10 ? Math.floor(t / 1000) : t
}

function price(c, source) {
  const o = parseFloat(c.open != null ? c.open : c.close)
  const h = parseFloat(c.high)
  const l = parseFloat(c.low)
  const close = parseFloat(c.close)
  if (source === 'HL2') return (h + l) / 2
  if (source === 'OHLC4') return (o + h + l + close) / 4
  return (h + l + close) / 3
}

function lineStyleValue(style) {
  switch (style) {
    case 'dotted': return LineStyle.Dotted
    case 'dashed': return LineStyle.Dashed
    case 'largeDashed': return LineStyle.LargeDashed
    case 'sparseDotted': return LineStyle.SparseDotted
    case 'solid':
    default: return LineStyle.Solid
  }
}

function withAlpha(color, alpha) {
  const a = Math.max(0, Math.min(1, alpha))
  if (typeof color !== 'string') return `rgba(176,141,87,${a})`
  if (color.startsWith('#')) {
    const clean = color.replace('#', '')
    if (clean.length === 6) {
      const bigint = parseInt(clean, 16)
      if (!Number.isNaN(bigint)) {
        return `rgba(${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}, ${a})`
      }
    }
  }
  if (color.startsWith('rgb')) {
    return color.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/, `rgba($1, $2, $3, ${a})`)
  }
  return color
}

export function calcVwap(candles, config) {
  const aggBars = Math.max(1, Math.floor(config.aggBars ?? 30))
  const priceSource = String(config.priceSource ?? 'HLC3')
  const showBands = Boolean(config.showBands ?? true)

  const window = []
  const vwap = []
  const band1Upper = []
  const band1Lower = []
  const band2Upper = []
  const band2Lower = []
  const band3Upper = []
  const band3Lower = []
  const fillData = []

  for (const c of candles) {
    const t = toSeconds(c.time)
    const v = parseFloat(c.volume ?? 0)
    const p = price(c, priceSource)
    window.push({ p, v })
    while (window.length > aggBars) window.shift()

    let sumPV = 0
    let sumV = 0
    for (const { p: wp, v: wv } of window) {
      if (wv <= 0) continue
      sumPV += wp * wv
      sumV += wv
    }

    const vwapVal = sumV <= 0 ? p : sumPV / sumV

    vwap.push({ time: t, value: round(vwapVal, 6) })

    if (showBands && sumV > 0) {
      let variance = 0
      for (const { p: wp, v: wv } of window) {
        if (wv <= 0) continue
        const dev = wp - vwapVal
        variance += dev * dev * wv
      }
      const std = Math.sqrt(variance / sumV)
      band1Upper.push({ time: t, value: round(vwapVal + std, 6) })
      band1Lower.push({ time: t, value: round(vwapVal - std, 6) })
      band2Upper.push({ time: t, value: round(vwapVal + 2 * std, 6) })
      band2Lower.push({ time: t, value: round(vwapVal - 2 * std, 6) })
      band3Upper.push({ time: t, value: round(vwapVal + 3 * std, 6) })
      band3Lower.push({ time: t, value: round(vwapVal - 3 * std, 6) })
      fillData.push({
        time: t,
        upper: round(vwapVal + std, 6),
        lower: round(vwapVal - std, 6),
      })
    }
  }

  return {
    vwap,
    bands: {
      band1Upper,
      band1Lower,
      band2Upper,
      band2Lower,
      band3Upper,
      band3Lower,
    },
    fillData,
  }
}

function round(n, digits) {
  const mult = 10 ** digits
  return Math.round(n * mult) / mult
}

export const defaultConfig = {
  // Lógica
  aggBars: 30,
  priceSource: 'HLC3',
  showBands: true,
  showLabel: true,
  showPriceLabel: true,

  // Dashboard / Panel
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},

  // Estilo Quantum
  color: '#B08D57',
  lineWidth: 1.5,
  lineStyle: 'solid',
  bandColor: '#B08D57',
  bandLineWidth: 1,
  bandLineStyle: 'dashed',
  bandFillOpacity: 0.10,
}

function cleanValues(arr) {
  if (!Array.isArray(arr)) return []
  const sorted = arr
    .filter(d => d && d.time != null && d.time !== '' && Number.isFinite(d.value))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const d of sorted) {
    if (out.length > 0 && out[out.length - 1].time === d.time) out[out.length - 1] = d
    else out.push(d)
  }
  return out
}

function cleanCloudData(arr) {
  if (!Array.isArray(arr)) return []
  const sorted = arr
    .filter(d => d && d.time != null && d.time !== '' && Number.isFinite(d.upper) && Number.isFinite(d.lower))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const d of sorted) {
    if (out.length > 0 && out[out.length - 1].time === d.time) out[out.length - 1] = d
    else out.push(d)
  }
  return out
}

export function render(chart, candleSeries, result, config, api) {
  if (!result || !result.vwap) return
  const cfg = { ...defaultConfig, ...(config || {}) }
  const mainColor = cfg.color
  const bandColor = cfg.bandColor
  const lineStyle = lineStyleValue(cfg.lineStyle)
  const bandLineStyle = lineStyleValue(cfg.bandLineStyle)
  const lineWidth = cfg.lineWidth ?? 1.5
  const bandLineWidth = cfg.bandLineWidth ?? 1

  const showPriceLabel = cfg.showPriceLabel ?? cfg.showLabel ?? true

  const mainSeries = api.addSeries(c => c.addLineSeries({
    title: 'VWAP',
    color: mainColor,
    lineWidth,
    lineStyle,
    lastValueVisible: showPriceLabel,
    priceLineVisible: false,
  }))
  const cleanVwap = cleanValues(result.vwap)
  if (mainSeries) mainSeries.setData(cleanVwap)

  if (cfg.showBands) {
    const defs = [
      { key: 'band1Upper', title: '+1σ', barsFromLast: 1, offsetY: 0 },
      { key: 'band1Lower', title: '-1σ', barsFromLast: 1, offsetY: 0 },
      { key: 'band2Upper', title: '+2σ', barsFromLast: 2, offsetY: 0 },
      { key: 'band2Lower', title: '-2σ', barsFromLast: 2, offsetY: 0 },
      { key: 'band3Upper', title: '+3σ', barsFromLast: 3, offsetY: 0 },
      { key: 'band3Lower', title: '-3σ', barsFromLast: 3, offsetY: 0 },
    ]

    const cleanBands = {}
    for (const { key, title } of defs) {
      cleanBands[key] = cleanValues(result.bands[key])
    }

    for (const { key, title } of defs) {
      const s = api.addSeries(c => c.addLineSeries({
        title: `VWAP ${title}`,
        color: bandColor,
        lineWidth: bandLineWidth,
        lineStyle: bandLineStyle,
        lastValueVisible: false,
        priceLineVisible: false,
      }))
      if (s) s.setData(cleanBands[key])
    }

    // Placas in-chart al final de cada banda, escalonadas horizontalmente para evitar colisión
    defs.forEach(({ key, title, barsFromLast, offsetY }) => {
      const data = cleanBands[key]
      const last = data[data.length - 1]
      if (last) {
        api.addLabel({
          barsFromLast,
          price: last.value,
          text: `VWAP ${title}`,
          color: bandColor,
          bgColor: withAlpha(bandColor, cfg.bandFillOpacity ?? 0.10),
          boxStyle: 'plaque',
          isBull: true,
          offsetY,
        })
      }
    })

    // Relleno suave entre las bandas de 1σ
    const fillOpacity = cfg.bandFillOpacity ?? 0.10
    if (fillOpacity > 0 && result.fillData.length > 0) {
      const fillSeries = api.addCustomSeries(new BandFillSeries(), {
        color: 'rgba(0,0,0,0)',
        fillColor: 'rgba(0,0,0,0)',
        lineWidth: 0,
        lastValueVisible: false,
        bandColor,
        bandFillOpacity: fillOpacity,
      })
      if (fillSeries) fillSeries.setData(cleanCloudData(result.fillData))
    }
  }

  // Último valor para el badge
  const last = result.vwap[result.vwap.length - 1]
  const lastCandle = candleSeries && candleSeries.data && candleSeries.data().slice(-1)[0]
  const lastPrice = lastCandle ? lastCandle.close : null
  return {
    lastValue: last?.value ?? null,
    lastPrice,
  }
}

export default {
  id: 'vwap',
  name: 'VWAP',
  version: '1.1.0',
  defaultConfig,
  detect: calcVwap,
  render,
  BadgeComponent,
  PanelComponent,
}
